import { type QueryClient, useQuery } from '@tanstack/react-query';
import { api, ApiError, type Me } from './api.ts';
import { APPROVER_NOMINEE } from './navigation.ts';

export const ME_KEY = ['auth', 'me'];

/** Quem está logado. `null` quando não há sessão. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/** A pessoa tem a permissão em algum escopo? (para mostrar menus e botões) */
export function can(me: Me | null | undefined, permission: string): boolean {
  if (permission === APPROVER_NOMINEE) return !!me?.approver;
  return !!me?.permissions.some((p) => p.permission === permission);
}

/** A permissão vale no grupo inteiro? */
export function canInGroup(me: Me | null | undefined, permission: string): boolean {
  return !!me?.permissions.some(
    (p) => p.permission === permission && p.scopes.some((s) => s.scopeType === 'group'),
  );
}

export function loginUrl(returnTo = window.location.pathname): string {
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export async function logout(): Promise<void> {
  const res = await api.post<{ logoutUrl: string | null }>('/auth/logout');
  window.location.assign(res.logoutUrl ?? '/login');
}

/**
 * Sigilo ao trocar de sessão ou de permissões (v3.1): os dados guardados na tela são descartados,
 * para nada calculado com outro perfil (por exemplo, com pessoal) continuar à vista. O servidor
 * confere as permissões a cada requisição; isto só evita mostrar o que já estava carregado.
 */
export function dropDataOnAccessChange(client: QueryClient) {
  let last: string | undefined;
  return client.getQueryCache().subscribe((event) => {
    // Só a consulta de quem está logado: as outras de `auth` (como as opções do Ver como, ADR 0103)
    // têm outro formato e quebravam aqui.
    if (event.type !== 'updated' || !isMeKey(event.query.queryKey)) return;
    const me = event.query.state.data as Me | null | undefined;
    if (me === undefined) return;
    const signature = JSON.stringify(me ? [me.user.id, me.permissions] : null);
    if (last !== undefined && signature !== last) {
      client.removeQueries({ predicate: (q) => !isMeKey(q.queryKey) });
    }
    last = signature;
  });
}

function isMeKey(key: readonly unknown[]): boolean {
  return key.length === ME_KEY.length && key.every((k, i) => k === ME_KEY[i]);
}
